<script setup lang="ts">
import { useVModel } from '@vueuse/core'

import DateComparison from '../components/DateComparison.vue'
import PongoFormComparison from '../components/PongoFormComparison.vue'
import AudienceDisclosure from '@/views/audience/components/AudienceDisclosure.vue'

import type { AudienceRule, CustomerTag } from '@/types/types'
import type { HighlightInfoFn } from '../composables/rulesMatcher'

const { relativeOperators, isRuleValueValid, isAudienceRuleWithParameters } = useAudience()
const { t } = useI18n()
const props = withDefaults(
  defineProps<{
    rules?: Record<string, AudienceRule>
    classes: any
    disabled: boolean
    getHighlightInfo: HighlightInfoFn
  }>(),
  {
    rules: () => ({})
  }
)

const rules = useVModel(props, 'rules')

const emit = defineEmits<{
  (e: 'hasRulesError', value: boolean): void
}>()

const lastValidCheckinHighlight = computed(() => props.getHighlightInfo('lastValidCheckin'))
const checkinCountHighlight = computed(() => props.getHighlightInfo('checkinCount'))
const averageCheckinCountHighlight = computed(() => props.getHighlightInfo('averageCheckinCount'))
const registrationDateHighlight = computed(() => props.getHighlightInfo('registrationDate'))
const tagsHighlight = computed(() => props.getHighlightInfo('tags'))

const isButtonDisplayed = ref(true)

const addAdditionalRule = () => {
  if (!isAudienceRuleWithParameters(rules.value.checkinCount)) {
    rules.value.checkinCount = {
      ...rules.value.checkinCount,
      parameters: {
        date_filter: {
          value: [],
          operator: '',
          type: ''
        }
      }
    }
  }
  isButtonDisplayed.value = false
}

const showRelativeButton = computed(() => {
  const isFormReadOnly = props.disabled === true
  const isMainRuleSet = isRuleValueValid(rules.value.checkinCount.value[0])
  const hasAdditionalRuleValue =
    isAudienceRuleWithParameters(rules.value.checkinCount) &&
    isRuleValueValid(rules.value.checkinCount.parameters.date_filter.value)

  if (!isButtonDisplayed.value || hasCheckinCountInputErrors.value) {
    return false
  }

  if (isFormReadOnly && isMainRuleSet && !hasAdditionalRuleValue) {
    return false
  }

  if (!isFormReadOnly && isMainRuleSet && hasAdditionalRuleValue) {
    return false
  }

  if (isFormReadOnly && isMainRuleSet && hasAdditionalRuleValue) {
    return false
  }

  if (
    !isFormReadOnly &&
    isMainRuleSet &&
    !hasAdditionalRuleValue &&
    isAudienceRuleWithParameters(rules.value.checkinCount) &&
    rules.value.checkinCount.parameters.date_filter.operator
  ) {
    return false
  }

  return true
})

const displayRelativeDateBlock = computed(() => {
  const isFormReadOnly = props.disabled === true
  const isRuleValueSet = !!rules.value.checkinCount.value[0]
  const hasAdditionalRuleValue =
    isAudienceRuleWithParameters(rules.value.checkinCount) &&
    isRuleValueValid(rules.value.checkinCount.parameters.date_filter.value)

  if (!isRuleValueSet && isFormReadOnly) {
    return false
  }

  if (!hasAdditionalRuleValue && isFormReadOnly && !isRuleValueSet) {
    return false
  }

  if (isFormReadOnly && isRuleValueSet && !hasAdditionalRuleValue) {
    return false
  }

  return true
})

const tags = inject('channelTags') as CustomerTag[]

const visitComparisonKeys = ['checkinCount', 'averageCheckinCount']

const visitCalendarKeys = ['lastValidCheckin']

const toggleValue = (ruleKey: string, value: any) => {
  if (!rules.value[ruleKey]) {
    return
  }

  const set = new Set((rules.value[ruleKey]?.value as any[]) || [])
  if (set.has(value)) {
    set.delete(value)
  } else {
    set.add(value)
  }

  rules.value[ruleKey] = { ...rules.value[ruleKey], value: Array.from(set) }
}

const hasCheckinCountInputErrors = computed(() => {
  if (
    isAudienceRuleWithParameters(rules.value.checkinCount) &&
    !isRuleValueValid(rules.value.checkinCount.value) &&
    isRuleValueValid(rules.value.checkinCount.parameters?.date_filter?.value)
  ) {
    return true
  }
  return false
})

const resetCheckinCount = () => {
  rules.value.checkinCount.parameters = {
    date_filter: {
      value: [],
      type: '',
      operator: ''
    }
  }
  isButtonDisplayed.value = true
}

const activeFiltersCount = computed(() => {
  let count = 0
  if (Array.isArray(rules.value.lastValidCheckin?.value) && rules.value.lastValidCheckin.value.length > 0) count++
  if (Array.isArray(rules.value.checkinCount?.value) && rules.value.checkinCount.value.length > 0) count++
  if (Array.isArray(rules.value.averageCheckinCount?.value) && rules.value.averageCheckinCount.value.length > 0) count++
  if (Array.isArray(rules.value.registrationDate?.value) && rules.value.registrationDate.value.length > 0) count++
  if (Array.isArray(rules.value.tags?.value) && rules.value.tags.value.length > 0) count++
  return count
})
</script>

<template>
  <AudienceDisclosure
    :title="t('pages.audience.audienceCreatePage.block.visit.title')"
    :subtitle="t('pages.audience.audienceCreatePage.block.visit.description')"
    icon="HeroCalendarIconOutline"
    :active-filters-count="activeFiltersCount"
  >
    <template v-for="key in visitCalendarKeys" :key="key">
      <div
        :class="[
          classes.rowGrid,
          {
            'bg-blue-50/30 rounded-lg p-3 ring-1 ring-blue-600':
              key === 'lastValidCheckin' && lastValidCheckinHighlight.hasDefined
          }
        ]"
      >
        <label :for="key" :class="classes.label">
          {{ t(`pages.audience.audienceCreatePage.block.visit.${key}.label`) }}
        </label>
        <DateComparison
          :id="key"
          :disabled="disabled"
          :class="classes.inputs"
          :rule="rules[key]"
          :name="key"
          is-relative-rule
          :use-direct-rule="true"
        />
      </div>
    </template>
    <template v-for="key in visitComparisonKeys" :key="key">
      <div
        :class="[
          classes.rowGrid,
          {
            'bg-blue-50/30 rounded-lg p-3 ring-1 ring-blue-600':
              (key === 'checkinCount' && checkinCountHighlight.hasDefined) ||
              (key === 'averageCheckinCount' && averageCheckinCountHighlight.hasDefined)
          }
        ]"
      >
        <label :class="[classes.label, 'flex items-center gap-1']">
          <span>
            {{ t(`pages.audience.audienceCreatePage.block.visit.${key}.label`) }}
          </span>
          <Tooltip v-if="key === 'averageCheckinCount'">
            <TooltipTrigger as-child>
              <BaseIcon name="IconoirInfoCircle" class="h-4 w-4 cursor-pointer text-foreground-secondary" />
            </TooltipTrigger>
            <TooltipContent>{{
              t('pages.audience.audienceCreatePage.block.visit.averageVisit.tooltip')
            }}</TooltipContent>
          </Tooltip>
        </label>
        <PongoFormComparison
          :id="key"
          :class="classes.inputs"
          :disabled="disabled"
          :rule="rules[key]"
          :name="key"
          :min="0"
          :input-right-name="t('global.words.days')"
          :errors="key === 'checkinCount' ? hasCheckinCountInputErrors : null"
          @has-error="emit('hasRulesError', $event)"
        />
      </div>
      <div v-if="key === 'checkinCount' && displayRelativeDateBlock" class="grid grid-cols-10">
        <TreeNode class="col-span-8" content-class="w-full">
          <template #content>
            <Button
              v-if="showRelativeButton"
              ref="addConditionButton"
              class="tree-content gap-2"
              type="button"
              variant="ghost"
              :disabled="disabled"
              @click="addAdditionalRule"
            >
              <BaseIcon name="HeroPlusIconSolid" class="h-5 w-5" />
              {{ t('pages.audience.operators.date.conditionBtn') }}
            </Button>
            <DateComparison
              v-else
              is-relative-rule
              :operators="relativeOperators"
              :rule="rules.checkinCount"
              :disabled="disabled"
            />
          </template>
        </TreeNode>
        <Button
          v-if="!showRelativeButton && !disabled"
          variant="ghost"
          size="icon"
          class="col-start-10 aspect-square w-fit justify-self-end text-muted-foreground hover:text-foreground-secondary"
          @click="resetCheckinCount"
        >
          <BaseIcon name="HeroTrashIconOutline" class="h-5 w-5" />
        </Button>
      </div>
    </template>
    <div
      :class="[
        classes.rowGrid,
        {
          'bg-blue-50/30 rounded-lg p-3 ring-1 ring-blue-600': registrationDateHighlight.hasDefined
        }
      ]"
    >
      <label for="registrationDate" :class="classes.label">
        {{ t(`pages.audience.audienceCreatePage.block.visit.registrationDate.label`) }}
      </label>
      <DateComparison
        id="registrationDate"
        :disabled="disabled"
        :class="classes.inputs"
        :rule="rules.registrationDate"
        name="registrationDate"
        label=""
        is-relative-rule
        :use-direct-rule="true"
      />
    </div>
    <div
      :class="[
        classes.rowGrid,
        {
          'bg-blue-50/30 rounded-lg p-3 ring-1 ring-blue-600': tagsHighlight.hasDefined
        }
      ]"
    >
      <label for="channel" :class="classes.label">
        {{ t('pages.audience.audienceCreatePage.block.visit.tags.label') }}
      </label>
      <div :class="classes.checkboxesContainer">
        <AppCheckboxField
          v-for="tag in tags"
          :id="`tag-${tag.id}`"
          :key="tag.id"
          :disabled="disabled"
          :model-value="Array.isArray(rules.tags?.value) && rules.tags.value.includes(tag.id)"
          @update:model-value="() => toggleValue('tags', tag.id)"
        >
          <template #label>
            <BaseChip :color="tag.color" :text="tag.name" />
          </template>
        </AppCheckboxField>
      </div>
    </div>
  </AudienceDisclosure>
</template>
